import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import type {
  ConfirmDeliveryInput,
  CreateDeliverableInput,
  CreateEventInput,
  CreatePhaseInput,
  CreateProjectInput,
  CreateSprintInput,
  Deliverable,
  PaginatedResult,
  Phase,
  PlanningDateRange,
  PlanningDateValidation,
  PlanningEvent,
  PlanningView,
  Project,
  ProjectAttachment,
  ProjectEditLog,
  ProjectHubSummary,
  ProjectListFilters,
  ProjectStatusAction,
  ProjectStatusLog,
  ProjectSummary,
  Sprint,
  ContractCount,
  UpdateDeliverableInput,
  UpdateEventInput,
  UpdatePhaseInput,
  UpdateProjectInput,
  UpdateSprintInput,
} from '@internal/orquestra-contract';
import type {
  AttachmentUploadProgress,
  OrquestraHealth,
  OrquestraWhoami,
} from './OrquestraApi.types';
import { OrquestraRequestError, toRequestError } from './OrquestraErrors';
import { uploadWithProgress } from './attachmentUpload';

export {
  AttachmentTooLargeError,
  AttachmentValidationError,
  GapDeleteConflictError,
  OrquestraRequestError,
} from './OrquestraErrors';

/** Monta a query string da listagem a partir dos filtros. */
function buildProjectsQuery(filters: ProjectListFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) {
    params.set(
      'status',
      Array.isArray(filters.status) ? filters.status.join(',') : filters.status,
    );
  }
  if (filters.startDateFrom) params.set('startDateFrom', filters.startDateFrom);
  if (filters.startDateTo) params.set('startDateTo', filters.startDateTo);
  if (filters.isDeleted !== undefined) {
    params.set('isDeleted', String(filters.isDeleted));
  }
  if (filters.skip !== undefined) params.set('skip', String(filters.skip));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.orderBy) params.set('orderBy', filters.orderBy);
  if (filters.orderDir) params.set('orderDir', filters.orderDir);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Client base que conversa com o plugin backend
 * (`plugins/orquestra-backend`), montado em `/api/orquestra/*`. Cobre
 * fundação, Projetos e Planning. Os métodos financeiros (Fase 3) ficam em
 * `OrquestraFinancialClient`, que estende esta classe — split para manter cada
 * arquivo sob o limite de 300 linhas.
 */
export class OrquestraClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    protected readonly fetchApi: FetchApi,
    private readonly identityApi: IdentityApi,
  ) {}

  protected async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('orquestra');
  }

  protected jsonInit(method: string, body?: unknown): RequestInit {
    return {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    };
  }

  protected async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (!response.ok) {
      throw await toRequestError(response, path);
    }
    return response.json() as Promise<T>;
  }

  async getHealth(): Promise<OrquestraHealth> {
    return this.request<OrquestraHealth>('/health');
  }

  async whoami(): Promise<OrquestraWhoami> {
    return this.request<OrquestraWhoami>('/whoami');
  }

  async listProjects(
    filters: ProjectListFilters = {},
  ): Promise<PaginatedResult<Project>> {
    return this.request<PaginatedResult<Project>>(
      `/projects${buildProjectsQuery(filters)}`,
    );
  }

  async getProject(id: number): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async createProject(input: CreateProjectInput): Promise<Project> {
    return this.request<Project>('/projects', this.jsonInit('POST', input));
  }

  async updateProject(id: number, input: UpdateProjectInput): Promise<Project> {
    return this.request<Project>(
      `/projects/${id}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteProject(
    id: number,
    justification?: string,
  ): Promise<{ action: string; message: string }> {
    return this.request(
      `/projects/${id}`,
      this.jsonInit('DELETE', { justification }),
    );
  }

  async getProjectSummary(): Promise<ProjectSummary> {
    return this.request<ProjectSummary>('/projects/summary');
  }

  async getContractCount(
    contract: string,
    excludeId?: number,
  ): Promise<ContractCount> {
    const params = new URLSearchParams({ contract });
    if (excludeId !== undefined) params.set('excludeId', String(excludeId));
    return this.request<ContractCount>(`/projects/contract-count?${params}`);
  }

  async changeProjectStatus(
    id: number,
    action: ProjectStatusAction,
    justification?: string,
  ): Promise<Project> {
    return this.request<Project>(
      `/projects/${id}/${action}`,
      this.jsonInit('POST', { justification }),
    );
  }

  async getStatusHistory(id: number): Promise<ProjectStatusLog[]> {
    return this.request<ProjectStatusLog[]>(`/projects/${id}/history`);
  }

  async getEditLog(id: number): Promise<ProjectEditLog[]> {
    return this.request<ProjectEditLog[]>(`/projects/${id}/edit-log`);
  }

  async getHubSummary(id: number): Promise<ProjectHubSummary> {
    return this.request<ProjectHubSummary>(`/projects/${id}/hub-summary`);
  }

  async getPlanning(projectId: number): Promise<PlanningView> {
    return this.request<PlanningView>(`/projects/${projectId}/planning`);
  }

  async createPhase(
    projectId: number,
    input: CreatePhaseInput,
  ): Promise<Phase> {
    return this.request<Phase>(
      `/projects/${projectId}/phases`,
      this.jsonInit('POST', input),
    );
  }

  async updatePhase(
    projectId: number,
    phaseId: number,
    input: UpdatePhaseInput,
  ): Promise<Phase> {
    return this.request<Phase>(
      `/projects/${projectId}/phases/${phaseId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deletePhase(
    projectId: number,
    phaseId: number,
    deletionReason: string,
  ): Promise<void> {
    await this.request(
      `/projects/${projectId}/phases/${phaseId}`,
      this.jsonInit('DELETE', { deletionReason }),
    );
  }

  async validatePhaseDates(
    projectId: number,
    phaseId: number,
    range: PlanningDateRange,
  ): Promise<PlanningDateValidation> {
    return this.request<PlanningDateValidation>(
      `/projects/${projectId}/phases/${phaseId}/validate-dates`,
      this.jsonInit('POST', {
        startDate: range.startDate,
        endDate: range.endDate ?? null,
      }),
    );
  }

  async createSprint(
    projectId: number,
    phaseId: number,
    input: CreateSprintInput,
  ): Promise<Sprint> {
    return this.request<Sprint>(
      `/projects/${projectId}/phases/${phaseId}/sprints`,
      this.jsonInit('POST', input),
    );
  }

  async updateSprint(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: UpdateSprintInput,
  ): Promise<Sprint> {
    return this.request<Sprint>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteSprint(
    projectId: number,
    phaseId: number,
    sprintId: number,
    deletionReason: string,
  ): Promise<void> {
    await this.request(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}`,
      this.jsonInit('DELETE', { deletionReason }),
    );
  }

  async confirmDeliverable(
    projectId: number,
    deliverableId: number,
    input: ConfirmDeliveryInput,
  ): Promise<Deliverable> {
    return this.request<Deliverable>(
      `/projects/${projectId}/deliverables/${deliverableId}/confirm`,
      this.jsonInit('PATCH', input),
    );
  }

  async revertDeliverable(
    projectId: number,
    phaseId: number,
    sprintId: number,
    deliverableId: number,
  ): Promise<Deliverable> {
    return this.request<Deliverable>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}` +
        `/deliverables/${deliverableId}/revert`,
      this.jsonInit('POST'),
    );
  }

  async toggleEvent(
    projectId: number,
    eventId: number,
  ): Promise<PlanningEvent> {
    return this.request<PlanningEvent>(
      `/projects/${projectId}/events/${eventId}/toggle`,
      this.jsonInit('PATCH'),
    );
  }

  /**
   * Executa uma requisição e retorna `null` quando o backend responde 404,
   * mantendo o contrato "não encontrado → null" utilizado nos services.
   * Demais respostas não-2xx continuam propagando {@link OrquestraRequestError}.
   */
  protected async requestNullable<T>(
    path: string,
    init?: RequestInit,
  ): Promise<T | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw await toRequestError(response, path);
    }
    return response.json() as Promise<T>;
  }

  /**
   * Executa uma requisição de remoção retornando `false` quando o backend
   * responde 404 (recurso já ausente) e `true` em qualquer sucesso 2xx.
   * Demais respostas não-2xx continuam propagando {@link OrquestraRequestError}.
   */
  protected async requestDelete(path: string): Promise<boolean> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'DELETE',
    });
    if (response.status === 404) {
      return false;
    }
    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.json())?.error ?? '';
      } catch {
        // corpo sem JSON — mantém detalhe vazio
      }
      throw new OrquestraRequestError(
        detail || `Falha ao remover recurso (${response.status}).`,
        response.status,
      );
    }
    return true;
  }

  /**
   * Lista os eventos de uma sprint específica (bug fix — Onda 1). A rota
   * simétrica ao `POST` mantém o path completo `projects/phases/sprints`
   * para reaproveitar contexto e políticas de autorização já existentes.
   */
  async listEvents(
    projectId: number,
    phaseId: number,
    sprintId: number,
  ): Promise<PlanningEvent[]> {
    return this.request<PlanningEvent[]>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}/events`,
    );
  }

  /** Cria um evento vinculado à sprint informada. */
  async createEvent(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: CreateEventInput,
  ): Promise<PlanningEvent> {
    return this.request<PlanningEvent>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}/events`,
      this.jsonInit('POST', input),
    );
  }

  /**
   * Atualiza um evento pelo id. Retorna `null` quando o backend responde 404
   * (paridade com o padrão de "não encontrado" do service).
   */
  async updateEvent(
    eventId: number,
    input: UpdateEventInput,
  ): Promise<PlanningEvent | null> {
    return this.requestNullable<PlanningEvent>(
      `/events/${eventId}`,
      this.jsonInit('PATCH', input),
    );
  }

  /** Remove um evento pelo id. `false` indica recurso já ausente (404). */
  async deleteEvent(eventId: number): Promise<boolean> {
    return this.requestDelete(`/events/${eventId}`);
  }

  /** Lista as entregas de uma sprint específica (bug fix — Onda 1). */
  async listDeliverables(
    projectId: number,
    phaseId: number,
    sprintId: number,
  ): Promise<Deliverable[]> {
    return this.request<Deliverable[]>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}/deliverables`,
    );
  }

  /** Cria uma entrega vinculada à sprint informada. */
  async createDeliverable(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: CreateDeliverableInput,
  ): Promise<Deliverable> {
    return this.request<Deliverable>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}/deliverables`,
      this.jsonInit('POST', input),
    );
  }

  /**
   * Atualiza uma entrega pelo id. Retorna `null` quando o backend responde 404
   * (paridade com o padrão de "não encontrado" do service).
   */
  async updateDeliverable(
    deliverableId: number,
    input: UpdateDeliverableInput,
  ): Promise<Deliverable | null> {
    return this.requestNullable<Deliverable>(
      `/deliverables/${deliverableId}`,
      this.jsonInit('PATCH', input),
    );
  }

  /** Remove uma entrega pelo id. `false` indica recurso já ausente (404). */
  async deleteDeliverable(deliverableId: number): Promise<boolean> {
    return this.requestDelete(`/deliverables/${deliverableId}`);
  }

  /** Lista os anexos do projeto (RF4.6). */
  async listAttachments(projectId: number): Promise<ProjectAttachment[]> {
    return this.request<ProjectAttachment[]>(
      `/projects/${projectId}/attachments`,
    );
  }

  /**
   * Faz o upload multipart de um anexo emitindo progresso via `XMLHttpRequest`
   * (RF4.7). Mapeia 413 para {@link AttachmentTooLargeError} e 400 para
   * {@link AttachmentValidationError}; demais erros viram
   * {@link OrquestraRequestError}.
   */
  async uploadAttachment(
    projectId: number,
    file: File,
    onProgress?: (progress: AttachmentUploadProgress) => void,
  ): Promise<ProjectAttachment> {
    return this.uploadFile<ProjectAttachment>(
      `/projects/${projectId}/attachments`,
      file,
      onProgress,
      `Falha de rede ao enviar anexo para o projeto ${projectId}.`,
    );
  }

  protected async uploadFile<T>(
    path: string,
    file: File,
    onProgress: ((progress: AttachmentUploadProgress) => void) | undefined,
    networkErrorMessage: string,
  ): Promise<T> {
    const [baseUrl, credentials] = await Promise.all([
      this.getBaseUrl(),
      this.identityApi.getCredentials(),
    ]);
    return uploadWithProgress<T>({
      url: `${baseUrl}${path}`,
      token: credentials?.token,
      file,
      onProgress,
      networkErrorMessage,
    });
  }

  /** Remove um anexo do projeto (RF4.11). */
  async deleteAttachment(
    projectId: number,
    attachmentId: number,
  ): Promise<void> {
    await this.deleteFile(`/projects/${projectId}/attachments/${attachmentId}`);
  }

  protected async deleteFile(path: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.json())?.error ?? '';
      } catch {
        // corpo sem JSON — mantém detalhe vazio
      }
      throw new OrquestraRequestError(
        detail || `Falha ao remover anexo (${response.status}).`,
        response.status,
      );
    }
  }

  /**
   * Compõe a URL absoluta de download do conteúdo do anexo (RF4.9). Opcional
   * `disposition=inline` habilita preview no navegador; padrão é anexo.
   */
  async getAttachmentDownloadUrl(
    projectId: number,
    attachmentId: number,
    disposition?: 'inline' | 'attachment',
  ): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const path = `${baseUrl}/projects/${projectId}/attachments/${attachmentId}/content`;
    return disposition ? `${path}?disposition=${disposition}` : path;
  }
}
